<template>
  <v-sheet
    class="top-bar d-flex"
    height="58px"
  >
    <div class="w-25" />
    <div class="w-50 d-flex px-4">
      <div class="my-auto">
        <HideRbac :permissions="['finalizeProfileTemplate']">
          <v-btn
            v-if="isTemplate"
            variant="outlined"
            rounded="lg"
            color="primary"
            size="small"
            :disabled="publishDisabled"
            @click="showModal = true"
          >
            Publish
          </v-btn>
        </HideRbac>
        <template v-if="hasEditAccess || checkRbacPermissions(['applyProfileTemplate'])">
          <v-btn
            v-if="!isTemplate && !isHrisProfile"
            variant="outlined"
            rounded="lg"
            color="primary"
            size="small"
            :disabled="applyTemplateDisabled"
            @click="setModal({ field: 'applyJobProfileTemplate', value: true })"
          >
            Apply Template
          </v-btn>
        </template>
        <HideRbac :permissions="['createDraftProfile']">
          <v-btn
            v-if="isHrisProfile && !jobProfile?.draft_profiles?.length"
            variant="outlined"
            rounded="lg"
            color="primary"
            size="small"
            :disabled="modalLoading"
            @click="setModal({ field: 'createDraftFromHris', value: true })"
          >
            Create Draft
          </v-btn>
        </HideRbac>
        <template v-if="hasEditAccess || checkRbacPermissions(['saveDraftProfileRevision'])">
          <FirstDraftButton
            v-if="authData.aiEnabled && !isHrisProfile && !isTemplate"
            :disabled="rescorePending || readOnly"
            variant="plain"
            class="pa-0 ml-4"
            height="24"
            @click="handleIterateWithFirstDraft"
          >
            <span class="text-button-2">
              {{ hasSummaryContent ? 'Iterate with First Draft' : 'Generate with First Draft' }}
            </span>
          </FirstDraftButton>
        </template>
        <v-btn
          variant="plain"
          min-width="0"
          class="text-button-2 my-auto pa-0 ml-4"
        >
          <ExportModalProfileEditor
            :ulid="jobProfile?.ulid"
            :title="jobProfile?.title"
            :is-data-loaded="!rescorePending"
            @export-tracking-event="handleTrackingEvent($event)"
          />
        </v-btn>
      </div>
    </div>

    <div class="w-25 d-flex justify-end">
      <HideRbac :permissions="['listDraftProfilesByProfileTemplate']">
        <TemplateUsageToggle
          v-if="authData.nextUiEnabled && isTemplate"
          class="pr-2 my-auto"
        />
      </HideRbac>
      <ApprovalsToggle class="pr-2 my-auto" />
      <HideRbac :permissions="['listFlsaSubmissions', 'listFlsaForms']">
        <FlsaToggle class="pr-2 my-auto" />
      </HideRbac>
      <HistoryToggle
        v-if="canShowHistoryToggle"
        class="pr-2 my-auto"
      />
      <CompareJobsToggle class="pr-2 my-auto" />
      <HideRbac :permissions="['cordComments']">
        <CommentsToggle
          v-if="!isHrisProfile"
          class="pr-2 my-auto"
        />
      </HideRbac>

      <AccessListToggle
        v-if="!isHrisProfile && !isTemplate"
        class="pr-2 my-auto"
      />

      <v-menu
        v-if="canDeleteTemplate || canDeleteDraft || checkRbacPermissions(['adminScoreBreakdown'])"
        location="bottom end"
        transition="scale-transition"
      >
        <template #activator="{ props: menuProps }">
          <v-btn
            v-bind="menuProps"
            class="my-auto mr-2"
            color="textDarkest"
            icon
            size="small"
            variant="text"
          >
            <v-icon icon="$dots-vertical" />
          </v-btn>
        </template>
        <v-list density="compact">
          <v-list-item
            v-if="canDeleteTemplate || canDeleteDraft"
            class="text-body-2 text-textDarkest"
            @click="showDeleteModal = true"
          >
            <v-list-item-title>Delete</v-list-item-title>
          </v-list-item>
          <HideRbac :permissions="['adminScoreBreakdown']">
            <v-list-item
              class="text-body-2 text-textDarkest"
              @click="openScoreBreakdown"
            >
              <v-list-item-title>Score Breakdown</v-list-item-title>
            </v-list-item>
          </HideRbac>
        </v-list>
      </v-menu>
    </div>
  </v-sheet>

  <Modal
    :show="showModal"
    cancel-text="Cancel"
    action-text="Publish"
    @close="showModal = false"
    @action="handlePublish"
  >
    <template #title>
      Publish Template
    </template>
    <template #content>
      Publishing this template makes its current content available when creating new job profiles.
    </template>
  </Modal>

  <ApplyJobProfileTemplateModal @applied="handleTemplateApplied" />

  <DeleteModal
    :show="showDeleteModal"
    :name="jobProfile ? jobProfile.title : ''"
    :document-type="isTemplate ? 'template' : 'job profile'"
    :detailed-text="isTemplate ? 'template' : 'job profile'"
    @close="showDeleteModal = false"
    @delete="handleDeleteConfirm"
  />

  <Modal
    :show="show.createDraftFromHris"
    cancel-text="Cancel"
    action-text="Create Draft"
    :disabled="modalLoading"
    @close="setModal({ field: 'createDraftFromHris', value: false })"
    @action="handleCreateDraft"
  >
    <template #title>
      Create Draft
    </template>
    <template #content>
      A new draft job profile with the same content will be created.
    </template>
  </Modal>

  <Modal
    :show="showIterateModal"
    cancel-text="Cancel"
    :action-text="hasSummaryContent ? 'Iterate' : 'Generate'"
    @close="showIterateModal = false"
    @action="handleIterateConfirm"
  >
    <template #title>
      {{ hasSummaryContent ? 'Iterate with First Draft' : 'Generate with First Draft' }}
    </template>

    <template #content>
      <p>Generating will overwrite the current content of this job profile.</p>
      <v-checkbox
        v-if="hasSummaryContent"
        v-model="useExistingSummary"
        density="compact"
        hide-details
        class="mt-3"
      >
        <template #label>
          <span class="text-body-2 ml-2">
            Use existing summary content to generate new content
          </span>
        </template>
      </v-checkbox>
    </template>
  </Modal>

  <v-snackbar
    :model-value="show.jobProfileGenerated"
    location="top right"
    timeout="-1"
    close-on-content-click
    color="success"
    rounded="lg"
    content-class="pa-2"
    @click="setModal({ field: 'jobProfileGenerated', value: false })"
  >
    <div>
      Profile content generated successfully.
    </div>
    <div>
      Job description might be outdated. Consider updating it.
    </div>
    <div class="mt-4">
      <v-btn
        variant="outlined"
        size="small"
        rounded="lg"
        color="white"
        @click="setModal({ field: 'updateProfileJobDescription', value: true })"
      >
        Update Job Description
      </v-btn>
    </div>
  </v-snackbar>
</template>

<script setup>
import { ref, computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppAuthData } from '@/stores/app/authData';
import { useRouter } from 'vue-router';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useAppLoading } from '@/stores/app/loading';
import { useAppRoute } from '@/stores/app/route';
import { useModals } from '@/stores/modals/modals';
import { useTracking } from '@/stores/tracking/tracking';
import Modal from '@/components/shared/Modal.vue';
import CommentsToggle from '@/components/document/rightPanel/CommentsToggle.vue';
import FlsaToggle from '@/components/document/rightPanel/FlsaToggle.vue';
import ApprovalsToggle from '@/components/document/rightPanel/ApprovalsToggle.vue';
import HistoryToggle from '@/components/document/rightPanel/HistoryToggle.vue';
import TemplateUsageToggle from '@/components/document/rightPanel/TemplateUsageToggle.vue';
import AccessListToggle from '@/components/document/rightPanel/AccessListToggle.vue';
import CompareJobsToggle from '@/components/document/rightPanel/CompareJobsToggle.vue';
import FirstDraftButton from '@ui/components/firstDraft/FirstDraftButton.vue';
import ExportModalProfileEditor from '@/components/jobProfiles/editor/ExportModalProfileEditor.vue';
import DeleteModal from '@/components/shared/DeleteModal.vue';
import { useFirstDraft } from '@/stores/document/firstDraft';
import { isEmptyHtml } from '@ui/helpers/htmlToText';
import ApplyJobProfileTemplateModal from '@/components/jobProfiles/editor/ApplyJobProfileTemplateModal.vue';
import HideRbac from '@/components/rbac/HideRbac.vue';
import { getJpEditorProfileType } from '@/helpers/jobProfileLibraryTracking';

const currentJobProfile = useCurrentJobProfile();
const {
  isTemplate, isHrisProfile, rescorePending, isAlreadyFinalized, jobProfile, summaryField, summaryFieldId, readOnly,
  hasEditAccess,
} = storeToRefs(currentJobProfile);

const { authData } = storeToRefs(useAppAuthData());
const { checkRbacPermissions } = useAppAuthData();

const { route } = storeToRefs(useAppRoute());

const { modalLoading } = storeToRefs(useAppLoading());
const { show } = storeToRefs(useModals());
const { setModal } = useModals();
const router = useRouter();

const showModal = ref(false);
const showDeleteModal = ref(false);
const showIterateModal = ref(false);
const useExistingSummary = ref(true);

const hasSummaryContent = computed(() => {
  if (!summaryField.value?.html) return false;
  return !isEmptyHtml(summaryField.value.html);
});

const publishDisabled = computed(() => (
  rescorePending.value || isAlreadyFinalized.value || readOnly.value
));

const applyTemplateDisabled = computed(() => (
  rescorePending.value || readOnly.value
));

const canDeleteTemplate = computed(() => !isHrisProfile.value
  && isTemplate.value
  && checkRbacPermissions(['createProfileTemplate']));

const canDeleteDraft = computed(() => !isTemplate.value
  && !isHrisProfile.value
  && (hasEditAccess.value || checkRbacPermissions(['deleteDraftProfile'])));

const canShowHistoryToggle = computed(() => {
  if (isHrisProfile.value) {
    return checkRbacPermissions(['listHrisProfileVersions']);
  }

  return hasEditAccess.value || checkRbacPermissions(['listDraftProfileRevisions']);
});

const openScoreBreakdown = () => {
  window.open(
    `/admin/job-profile/messages?ulid=${jobProfile.value?.ulid}&project_id=${authData.value.projectId}`,
    '_blank',
  );
};

const handleIterateWithFirstDraft = () => {
  useExistingSummary.value = true;
  showIterateModal.value = true;
};

const handleIterateConfirm = async () => {
  showIterateModal.value = false;
  await useFirstDraft().generateAndApplyJobProfile({
    ulid: jobProfile.value?.ulid,
    useExistingSummary: hasSummaryContent.value && useExistingSummary.value,
    summaryFieldId: summaryFieldId.value,
  });
  setModal({ field: 'jobProfileGenerated', value: true });
};

const handleCreateDraft = async () => {
  const sourceUlid = jobProfile.value?.ulid;
  const documentUlid = await currentJobProfile.createDraftFromHrisProfile();
  setModal({ field: 'createDraftFromHris', value: false });

  useTracking().jpEditorCreateDraftFromHris({
    ulid: sourceUlid,
    newUlid: documentUlid,
  });

  await router.push({ name: 'job-profile-view', params: { id: documentUlid } });
};

const handlePublish = async () => {
  await currentJobProfile.finalizeTemplate();
  showModal.value = false;

  useTracking().jpEditorPublish({
    ulid: jobProfile.value?.ulid,
    profileType: getJpEditorProfileType({
      isHris: isHrisProfile.value,
      isTemplate: isTemplate.value,
    }),
  });
};

const handleTemplateApplied = async ({ documentUlid, oldRevisionUlid }) => {
  await currentJobProfile.loadJobProfile({ ulid: documentUlid });

  setModal({
    field: 'jobProfileCompare',
    value: true,
    params: {
      oldSide: {
        isHrisProfile: false,
        profileUlid: documentUlid,
        revisionUlid: oldRevisionUlid,
        readOnly: readOnly.value,
      },
      newSide: {
        isHrisProfile: false,
        profileUlid: documentUlid,
        revisionUlid: jobProfile.value?.last_revision_ulid,
      },
    },
  });

  return router.replace({
    name: 'job-profile-view',
    params: { id: documentUlid },
    query: { ...(route.value?.query || {}) },
  });
};

const handleDeleteConfirm = async () => {
  const destination = isTemplate.value ? 'job-profile-templates' : 'draft-job-profiles';
  const deletedUlid = jobProfile.value?.ulid;
  const deletedProfileType = getJpEditorProfileType({
    isHris: isHrisProfile.value,
    isTemplate: isTemplate.value,
  });

  showDeleteModal.value = false;
  await currentJobProfile.deleteJobProfile();

  useTracking().jpEditorDelete({
    ulid: deletedUlid,
    profileType: deletedProfileType,
  });

  await router.push({ name: destination });
};

const handleTrackingEvent = ({ action, args }) => {
  useTracking()[action](args);
};
</script>
